import React from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import {
  DialogOverlay,
  DialogContainer,
  DialogBody,
  DialogFooter,
  StepperContainer,
  StepItem,
  StepCircle,
  StepErrorBadge,
  StepLabel,
  StepConnector,
  ConnectorHalf,
  ContentArea,
  Title,
  Description,
  ButtonDivider,
  ButtonContainer,
  PrimaryButton,
  SecondaryButton,
  ErrorMessage,
  PermissionEnableMessageWrapper,
  PermissionEnableMessageText,
  MicOptionsList,
  MicOptionRow,
  MicOptionLabel,
  ScreenShareStepsList,
  ScreenShareStepRow,
  ScreenShareStepHighlight
} from './PermissionDialog.styles';
import { ALL_STEPS, stepConfig } from './PermissionDialog.constants';
import usePermissionDialog from './usePermissionDialog';

interface ProctorPermissionDialogProps {
  onComplete: () => void;
  onCancel: () => void;
  initialScreenPermissionState?: 'prompt' | 'granted' | 'denied';
}

const ProctorPermissionDialog = ({
  onComplete,
  onCancel,
  initialScreenPermissionState
}: ProctorPermissionDialogProps) => {
  const {
    isCheckingPermissions,
    currentStepType,
    currentFullStepIndex,
    isLastStepCurrent,
    dashedConnectorIndex,
    stepPermissionStateByType,
    stepDismissedByType,
    cameraPermissionState,
    cameraDismissed,
    showGenericCameraError,
    cameraGranted,
    micPermissionState,
    micDismissed,
    showGenericMicError,
    micGranted,
    screenPermissionState,
    error,
    isTestingCamera,
    isTestingMic,
    testCamera,
    testMicrophone,
    handleNext,
    handleCancel
  } = usePermissionDialog({
    onComplete,
    onCancel,
    initialScreenPermissionState
  });

  if (isCheckingPermissions) {
    return (
      <DialogOverlay>
        <DialogContainer>
          <DialogBody>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px'
              }}
            >
              <div>Checking permissions...</div>
            </div>
          </DialogBody>
        </DialogContainer>
      </DialogOverlay>
    );
  }

  return (
    <DialogOverlay>
      <DialogContainer>
        <DialogBody>
          <StepperContainer>
            {ALL_STEPS.map((type, index) => {
              const step = stepConfig[type];
              // A step counts as "done" if we've moved past its position in
              // the full list - this covers both normal progression AND
              // steps that were silently skipped because permission was
              // already granted on mount.
              const completed = index < currentFullStepIndex;
              const active = index === currentFullStepIndex;
              let status = 'notdone';
              if (completed) {
                status = 'done';
              } else if (active) {
                status = 'current';
              }
              let iconSrc = step.iconInactive || step.iconCurrent;
              if (status === 'done') {
                iconSrc = step.iconDone;
              } else if (status === 'current') {
                iconSrc = step.iconCurrent;
              }
              const hasError =
                stepPermissionStateByType[type] === 'denied' ||
                stepDismissedByType[type];

              // Connector line to the NEXT step: fully solid dark for
              // segments already reached, solid light gray for segments not
              // yet reached, and half-solid/half-dashed dark for the one
              // "in progress" connector (see dashedConnectorIndex above).
              let connector = null;
              if (index < ALL_STEPS.length - 1) {
                const reached = index <= currentFullStepIndex;
                const color = reached ? '#3C4852' : '#E9EEF2';
                let leftDashed = false;
                let rightDashed = false;
                if (index === dashedConnectorIndex) {
                  leftDashed = isLastStepCurrent;
                  rightDashed = !isLastStepCurrent;
                }
                connector = (
                  <StepConnector>
                    <ConnectorHalf color={color} dashed={leftDashed} />
                    <ConnectorHalf color={color} dashed={rightDashed} />
                  </StepConnector>
                );
              }

              return (
                <React.Fragment key={type}>
                  <StepItem>
                    <StepCircle>
                      <Image
                        src={iconSrc}
                        alt={step.label}
                        width={34}
                        height={34}
                      />
                      {hasError && (
                        <StepErrorBadge>
                          <Image
                            src="/svgs/alert-circle-icon.svg"
                            alt="Error"
                            width={18}
                            height={18}
                          />
                        </StepErrorBadge>
                      )}
                    </StepCircle>
                    <StepLabel status={status}>{step.label}</StepLabel>
                  </StepItem>
                  {connector}
                </React.Fragment>
              );
            })}
          </StepperContainer>

          <ContentArea>
            {currentStepType === 'camera' && (
              <>
                <Title>Camera permission</Title>
                <Description>
                  Make sure the following is enabled before you begin
                </Description>
                <Image
                  src={
                    cameraPermissionState === 'denied'
                      ? '/svgs/camera-permission-denied.png'
                      : '/svgs/camera-permission-pop-up.svg'
                  }
                  alt="Camera Image"
                  width={452}
                  height={320}
                />

                {cameraDismissed && (
                  <PermissionEnableMessageWrapper>
                    <Image
                      src="/svgs/alert-circle.svg"
                      alt="Alert"
                      width={18}
                      height={18}
                    />
                    <PermissionEnableMessageText>
                      Enable this in your browser to start the test.
                    </PermissionEnableMessageText>
                  </PermissionEnableMessageWrapper>
                )}

                {showGenericCameraError && <ErrorMessage>{error}</ErrorMessage>}
              </>
            )}

            {currentStepType === 'mic' && (
              <>
                <Title>Audio permission</Title>
                <Description>
                  Make sure the following is enabled before you begin
                </Description>
                <Image
                  src={
                    micPermissionState === 'denied'
                      ? '/svgs/mic-permission-denied-pop-up.png'
                      : '/svgs/mic-permission-pop-up.svg'
                  }
                  alt="Microphone Image"
                  width={452}
                  height={320}
                />

                {micPermissionState !== 'denied' && (
                  <MicOptionsList>
                    <MicOptionRow>
                      Option 1: Click{' '}
                      <MicOptionLabel>
                        Allow while visiting the site
                      </MicOptionLabel>
                    </MicOptionRow>
                    <MicOptionRow>
                      Option 2: Click{' '}
                      <MicOptionLabel>Allow this time</MicOptionLabel>
                    </MicOptionRow>
                  </MicOptionsList>
                )}

                {micDismissed && (
                  <PermissionEnableMessageWrapper>
                    <Image
                      src="/svgs/alert-circle.svg"
                      alt="Alert"
                      width={18}
                      height={18}
                    />
                    <PermissionEnableMessageText>
                      Enable this in your browser to start the test.
                    </PermissionEnableMessageText>
                  </PermissionEnableMessageWrapper>
                )}

                {showGenericMicError && <ErrorMessage>{error}</ErrorMessage>}
              </>
            )}

            {currentStepType === 'screen' && (
              <>
                <Title>Screen sharing</Title>
                <Description>
                  Make sure the following is enabled before you begin
                </Description>
                <Image
                  src={
                    screenPermissionState === 'denied'
                      ? '/svgs/screen-share-access-denied-pop-up.svg'
                      : '/svgs/screen-share-pop-up.svg'
                  }
                  alt="Screen Share Image"
                  width={452}
                  height={320}
                />

                <ScreenShareStepsList>
                  <ScreenShareStepRow>
                    1. Click{' '}
                    <ScreenShareStepHighlight>
                      Entire Screen
                    </ScreenShareStepHighlight>{' '}
                    tab
                  </ScreenShareStepRow>
                  <ScreenShareStepRow>
                    2. Select the{' '}
                    <ScreenShareStepHighlight>screen</ScreenShareStepHighlight>
                  </ScreenShareStepRow>
                  <ScreenShareStepRow>
                    3. Click{' '}
                    <ScreenShareStepHighlight>Share</ScreenShareStepHighlight>
                  </ScreenShareStepRow>
                </ScreenShareStepsList>
              </>
            )}
          </ContentArea>
        </DialogBody>

        <ButtonDivider />

        <DialogFooter>
          <ButtonContainer>
            <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>

            {currentStepType === 'camera' && !cameraGranted && (
              <PrimaryButton onClick={testCamera} disabled={isTestingCamera}>
                {isTestingCamera ? 'Testing...' : 'Give permission'}
              </PrimaryButton>
            )}

            {currentStepType === 'camera' && cameraGranted && (
              <SecondaryButton onClick={testCamera} disabled={isTestingCamera}>
                {isTestingCamera ? 'Re-testing...' : 'Give permission'}
              </SecondaryButton>
            )}

            {currentStepType === 'mic' && !micGranted && (
              <PrimaryButton onClick={testMicrophone} disabled={isTestingMic}>
                {isTestingMic ? 'Testing...' : 'Give permission'}
              </PrimaryButton>
            )}

            {currentStepType === 'mic' && micGranted && (
              <SecondaryButton onClick={testMicrophone} disabled={isTestingMic}>
                {isTestingMic ? 'Re-testing...' : 'Give permission'}
              </SecondaryButton>
            )}

            {currentStepType === 'screen' && (
              <PrimaryButton onClick={handleNext}>
                Start Proctoring
              </PrimaryButton>
            )}
          </ButtonContainer>
        </DialogFooter>
      </DialogContainer>
    </DialogOverlay>
  );
};

ProctorPermissionDialog.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialScreenPermissionState: PropTypes.oneOf(['prompt', 'granted', 'denied'])
};

export default ProctorPermissionDialog;
